/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — "套餐管理" tab (task spec §6/§7).
 *
 * Task spec §6.2 — top "文件卡 / Tab Card" Plan selector (never a
 * marketing-site Pricing Card grid). Task spec §6.2 URL persistence —
 * the selected Plan Key is mirrored into `?plan_key=...` (owned by the
 * parent `PlanCreditsBillingPage`, passed down as `selectedPlanKey` /
 * `onSelectPlan`) so a page refresh keeps the same Plan selected.
 *
 * Task spec §18.1 — "只保存当前 section": Basic / Pricing+Credits /
 * Usage+Limits each have their own independent Save action, never one
 * page-wide submit.
 */
import { Alert, Button, Drawer, Empty, Form, Input, InputNumber, Radio, Select, Skeleton, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  platformCreateBillingPlan,
  platformDeleteBillingPlanPrice,
  platformGetPlanCreditsOverview,
  platformListBillingPlans,
  platformUpdateBillingPlan,
  platformUpsertBillingPlanPrice,
} from '../../api'
import type { BillingPlan, BillingPlanPrice, PlanCreditsOverview } from '../../types'
import { SectionCard, SummaryCard } from '../adminShared'
import { platformColors } from '../theme'
import {
  ACTIVE_CURRENCIES,
  CREDIT_VALIDITY_LABEL,
  EXHAUSTION_POLICY_LABEL,
  errMsg,
  formatCredits,
  formatMoneyMinor,
  formatPricePerCredit,
  majorNumberToMinor,
  minorToMajorNumber,
} from './billingShared'

export default function PlanManagementTab({
  selectedPlanKey,
  onSelectPlan,
}: {
  selectedPlanKey: string | null
  onSelectPlan: (key: string) => void
}) {
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [overview, setOverview] = useState<PlanCreditsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([platformListBillingPlans(), platformGetPlanCreditsOverview()])
      .then(([plansRes, overviewRes]) => {
        setPlans(plansRes.data)
        setOverview(overviewRes.data)
        if (!selectedPlanKey && plansRes.data.length > 0) {
          onSelectPlan(plansRes.data[0].key)
        }
      })
      .catch((err) => setError(errMsg(err, '加载套餐数据失败')))
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [])

  const selectedPlan = useMemo(
    () => plans.find((p) => p.key === selectedPlanKey) ?? plans[0] ?? null,
    [plans, selectedPlanKey],
  )

  const patchPlanLocally = (updated: BillingPlan) => {
    setPlans((prev) => prev.map((p) => (p.key === updated.key ? updated : p)))
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <SummaryCard title="套餐数量" value={overview?.total_plans ?? '—'} iconBg={platformColors.primarySoft} icon="📦" loading={loading} />
        <SummaryCard title="启用套餐" value={overview?.enabled_plans ?? '—'} iconBg={platformColors.successBg} icon="✅" loading={loading} />
        <SummaryCard
          title="默认套餐"
          value={overview?.default_plan_display_name ?? '未设置'}
          iconBg={platformColors.infoBg}
          icon="⭐"
          loading={loading}
        />
        <SummaryCard
          title="支持币种"
          value={overview?.supported_currencies?.join(' / ') || '—'}
          iconBg={platformColors.warningBg}
          icon="💱"
          loading={loading}
        />
        <SummaryCard
          title="积分相关活动"
          value={overview?.active_reward_and_promotion_count ?? '—'}
          hint="每日奖励 + 充值赠送规则 + 营销活动"
          iconBg={platformColors.primarySoft}
          icon="🎁"
          loading={loading}
        />
      </Space>

      {error && <Alert type="error" message={error} showIcon style={{ margin: '4px 0 12px' }} />}

      <SectionCard title="套餐管理" allowOverflow>
        <Typography.Paragraph type="secondary" style={{ margin: '0 4px 12px', fontSize: 12 }}>
          创建和管理套餐卡片，设置不同币种的价格、积分数量、有效期及相关规则。功能节点与
          Provider 通道默认授权请前往{' '}
          <Link to={selectedPlan ? `/platform/plan-auth-matrix?plan_key=${encodeURIComponent(selectedPlan.key)}` : '/platform/plan-auth-matrix'}>
            套餐功能授权
          </Link>
          。
        </Typography.Paragraph>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '0 4px 12px' }}>
          {loading && plans.length === 0 ? (
            <Skeleton.Button active style={{ width: 160, height: 92 }} />
          ) : (
            plans
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((plan) => (
                <PlanCard key={plan.key} plan={plan} selected={plan.key === selectedPlan?.key} onClick={() => onSelectPlan(plan.key)} />
              ))
          )}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            style={{
              minWidth: 150,
              border: `1px dashed ${platformColors.border}`,
              borderRadius: 10,
              background: 'transparent',
              color: platformColors.secondaryText,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              padding: '18px 12px',
            }}
          >
            <span style={{ fontSize: 20 }}>＋</span>
            <span style={{ fontSize: 13 }}>新增套餐</span>
          </button>
        </div>

        {selectedPlan ? (
          <PlanDetailPanel plan={selectedPlan} onPlanUpdated={patchPlanLocally} />
        ) : (
          !loading && <Empty description="暂无套餐，请先新增套餐" />
        )}
      </SectionCard>

      <AddPlanDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(plan) => {
          setPlans((prev) => [...prev, plan])
          onSelectPlan(plan.key)
        }}
      />
    </div>
  )
}

function PlanCard({ plan, selected, onClick }: { plan: BillingPlan; selected: boolean; onClick: () => void }) {
  const primaryPrice = plan.prices.find((p) => p.enabled) ?? plan.prices[0]
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minWidth: 150,
        textAlign: 'left',
        border: `1px solid ${selected ? platformColors.primary : platformColors.border}`,
        boxShadow: selected ? `0 0 0 1px ${platformColors.primary}` : undefined,
        borderRadius: 10,
        background: platformColors.surface,
        cursor: 'pointer',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: platformColors.primaryText }}>{plan.display_name}</span>
        <Tag color={plan.enabled ? 'green' : 'default'} style={{ marginRight: 0 }}>
          {plan.enabled ? '启用' : '停用'}
        </Tag>
      </div>
      <div style={{ fontSize: 13, color: platformColors.primaryText }}>
        {primaryPrice ? `${formatMoneyMinor(primaryPrice.amount_minor, primaryPrice.currency)} 起` : '未定价'}
      </div>
      <div style={{ fontSize: 11, color: platformColors.mutedText }}>
        基础积分 {formatCredits(plan.base_credits)}
        {plan.is_registration_default ? ' · 注册默认' : ''}
      </div>
    </button>
  )
}

function AddPlanDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (plan: BillingPlan) => void
}) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) form.resetFields()
  }, [open, form])

  const submit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      const res = await platformCreateBillingPlan({
        key: values.key,
        display_name: values.display_name,
        description: values.description || null,
        sort_order: values.sort_order ?? 0,
        is_registration_default: Boolean(values.is_registration_default),
      })
      message.success('套餐已创建')
      onCreated(res.data)
      onClose()
    } catch (err) {
      message.error(errMsg(err, '创建套餐失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer title="新增套餐" open={open} onClose={onClose} width={420} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="display_name" label="套餐名称" rules={[{ required: true, message: '请输入套餐名称' }]}>
          <Input placeholder="例如：标准版" />
        </Form.Item>
        <Form.Item
          name="key"
          label="Plan Key"
          extra="创建后不可修改，建议使用小写字母/下划线，例如 standard"
          rules={[
            { required: true, message: '请输入 Plan Key' },
            { pattern: /^[a-z][a-z0-9_]*$/, message: '仅支持小写字母、数字、下划线，且以字母开头' },
          ]}
        >
          <Input placeholder="例如：standard" />
        </Form.Item>
        <Form.Item name="description" label="描述">
          <Input.TextArea rows={3} placeholder="套餐说明，将展示给管理员/未来的定价页面" />
        </Form.Item>
        <Form.Item name="is_registration_default" label="设为注册默认套餐" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="sort_order" label="排序" initialValue={0}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>取消</Button>
        <Button type="primary" loading={submitting} onClick={() => void submit()}>
          创建套餐
        </Button>
      </div>
    </Drawer>
  )
}

function PlanDetailPanel({ plan, onPlanUpdated }: { plan: BillingPlan; onPlanUpdated: (plan: BillingPlan) => void }) {
  return (
    <div style={{ marginTop: 4 }}>
      <Tabs
        type="card"
        items={[
          { key: 'basic', label: '基本设置', children: <BasicSettingsForm plan={plan} onPlanUpdated={onPlanUpdated} /> },
          { key: 'pricing', label: '价格与积分', children: <PricingCreditsForm plan={plan} onPlanUpdated={onPlanUpdated} /> },
          { key: 'usage', label: '用量与超限', children: <UsageLimitsForm plan={plan} onPlanUpdated={onPlanUpdated} /> },
        ]}
      />
    </div>
  )
}

function BasicSettingsForm({ plan, onPlanUpdated }: { plan: BillingPlan; onPlanUpdated: (plan: BillingPlan) => void }) {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    form.setFieldsValue({
      display_name: plan.display_name,
      description: plan.description,
      enabled: plan.enabled,
      is_registration_default: plan.is_registration_default,
      sort_order: plan.sort_order,
    })
  }, [plan, form])

  const save = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      const res = await platformUpdateBillingPlan(plan.key, values)
      onPlanUpdated({ ...res.data, prices: plan.prices, credit_metrics: plan.credit_metrics })
      message.success('基本设置已保存')
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
      <Form.Item label="Plan Key">
        <Input value={plan.key} disabled />
      </Form.Item>
      <Form.Item name="display_name" label="套餐名称" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="description" label="描述">
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item name="enabled" label="启用状态" valuePropName="checked">
        <Switch checkedChildren="启用" unCheckedChildren="停用" />
      </Form.Item>
      <Form.Item name="is_registration_default" label="设为注册默认套餐" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="sort_order" label="排序">
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>
      <Button type="primary" loading={saving} onClick={() => void save()}>
        保存基本设置
      </Button>
    </Form>
  )
}

function PricingCreditsForm({ plan, onPlanUpdated }: { plan: BillingPlan; onPlanUpdated: (plan: BillingPlan) => void }) {
  const [creditForm] = Form.useForm()
  const [savingCredits, setSavingCredits] = useState(false)
  const [newPriceCurrency, setNewPriceCurrency] = useState<string | undefined>(undefined)
  const [newPriceAmount, setNewPriceAmount] = useState<number | null>(null)
  const [newPricePeriod, setNewPricePeriod] = useState('monthly')
  const [addingPrice, setAddingPrice] = useState(false)
  const [rowDrafts, setRowDrafts] = useState<Record<string, { amount: number; period: string; enabled: boolean }>>({})
  const [savingRow, setSavingRow] = useState<string | null>(null)
  const [previewCurrency, setPreviewCurrency] = useState<string | undefined>(plan.prices[0]?.currency)

  useEffect(() => {
    creditForm.setFieldsValue({ base_credits: Number(plan.base_credits), bonus_credits: Number(plan.bonus_credits) })
    setPreviewCurrency((prev) => prev ?? plan.prices[0]?.currency)
  }, [plan, creditForm])

  const totalCredits = Number(plan.base_credits) + Number(plan.bonus_credits)

  const availableCurrenciesForNewPrice = ACTIVE_CURRENCIES.filter((c) => !plan.prices.some((p) => p.currency === c))

  const saveCredits = async () => {
    const values = await creditForm.validateFields()
    setSavingCredits(true)
    try {
      const res = await platformUpdateBillingPlan(plan.key, {
        base_credits: values.base_credits,
        bonus_credits: values.bonus_credits,
      })
      onPlanUpdated({ ...res.data, prices: plan.prices, credit_metrics: plan.credit_metrics })
      message.success('积分设置已保存')
    } catch (err) {
      message.error(errMsg(err, '保存积分失败'))
    } finally {
      setSavingCredits(false)
    }
  }

  const addPrice = async () => {
    if (!newPriceCurrency || newPriceAmount === null) {
      message.warning('请选择币种并输入金额')
      return
    }
    setAddingPrice(true)
    try {
      const amountMinor = majorNumberToMinor(newPriceAmount, newPriceCurrency)
      const res = await platformUpsertBillingPlanPrice(plan.key, {
        currency: newPriceCurrency,
        amount_minor: amountMinor,
        period: newPricePeriod,
        enabled: true,
      })
      onPlanUpdated({ ...plan, prices: [...plan.prices, res.data] })
      setNewPriceCurrency(undefined)
      setNewPriceAmount(null)
      message.success('已添加币种价格')
    } catch (err) {
      message.error(errMsg(err, '添加价格失败'))
    } finally {
      setAddingPrice(false)
    }
  }

  const saveRow = async (price: BillingPlanPrice) => {
    const draft = rowDrafts[price.id]
    if (!draft) return
    setSavingRow(price.id)
    try {
      const res = await platformUpsertBillingPlanPrice(plan.key, {
        currency: price.currency,
        amount_minor: majorNumberToMinor(draft.amount, price.currency),
        period: draft.period,
        enabled: draft.enabled,
        sort_order: price.sort_order,
      })
      onPlanUpdated({ ...plan, prices: plan.prices.map((p) => (p.id === price.id ? res.data : p)) })
      setRowDrafts((d) => {
        const next = { ...d }
        delete next[price.id]
        return next
      })
      message.success('价格已更新')
    } catch (err) {
      message.error(errMsg(err, '更新价格失败'))
    } finally {
      setSavingRow(null)
    }
  }

  const deletePrice = async (price: BillingPlanPrice) => {
    try {
      await platformDeleteBillingPlanPrice(plan.key, price.id)
      onPlanUpdated({ ...plan, prices: plan.prices.filter((p) => p.id !== price.id) })
      message.success('已删除该币种价格')
    } catch (err) {
      message.error(errMsg(err, '删除价格失败'))
    }
  }

  const previewPrice = plan.prices.find((p) => p.currency === previewCurrency)

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '2 1 520px', minWidth: 420 }}>
        <Typography.Text strong>价格与积分配置</Typography.Text>
        <Table
          className="pf-data-table"
          size="small"
          rowKey="id"
          style={{ marginTop: 8 }}
          pagination={false}
          dataSource={plan.prices}
          columns={[
            { title: '货币 (Currency)', dataIndex: 'currency', width: 90 },
            {
              title: '销售价格',
              dataIndex: 'amount_minor',
              width: 130,
              render: (v: number, row) => (
                <InputNumber
                  size="small"
                  min={0}
                  step={0.01}
                  value={rowDrafts[row.id]?.amount ?? minorToMajorNumber(v, row.currency)}
                  onChange={(val) =>
                    setRowDrafts((d) => ({
                      ...d,
                      [row.id]: {
                        amount: val ?? 0,
                        period: d[row.id]?.period ?? row.period,
                        enabled: d[row.id]?.enabled ?? row.enabled,
                      },
                    }))
                  }
                  style={{ width: 100 }}
                />
              ),
            },
            { title: '基础积分', width: 90, render: () => formatCredits(plan.base_credits) },
            { title: '赠送积分', width: 90, render: () => formatCredits(plan.bonus_credits) },
            { title: '总积分', width: 90, render: () => formatCredits(totalCredits) },
            {
              title: '等效价格 / 积分',
              width: 120,
              render: (_: unknown, row: BillingPlanPrice) => formatPricePerCredit(row.amount_minor, row.currency, totalCredits),
            },
            {
              title: '状态',
              dataIndex: 'enabled',
              width: 90,
              render: (v: boolean, row) => (
                <Switch
                  size="small"
                  checked={rowDrafts[row.id]?.enabled ?? v}
                  onChange={(checked) =>
                    setRowDrafts((d) => ({
                      ...d,
                      [row.id]: {
                        amount: d[row.id]?.amount ?? minorToMajorNumber(row.amount_minor, row.currency),
                        period: d[row.id]?.period ?? row.period,
                        enabled: checked,
                      },
                    }))
                  }
                />
              ),
            },
            {
              title: '操作',
              width: 130,
              render: (_: unknown, row: BillingPlanPrice) => (
                <Space size={4}>
                  <Button
                    size="small"
                    type="link"
                    disabled={!rowDrafts[row.id]}
                    loading={savingRow === row.id}
                    onClick={() => saveRow(row)}
                  >
                    保存
                  </Button>
                  <Button size="small" type="link" danger onClick={() => void deletePrice(row)}>
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
        <Space style={{ marginTop: 10 }} wrap>
          <Select
            placeholder="添加币种"
            style={{ width: 110 }}
            value={newPriceCurrency}
            onChange={setNewPriceCurrency}
            options={availableCurrenciesForNewPrice.map((c) => ({ label: c, value: c }))}
          />
          <InputNumber placeholder="金额" min={0} step={0.01} value={newPriceAmount} onChange={setNewPriceAmount} style={{ width: 110 }} />
          <Select
            value={newPricePeriod}
            onChange={setNewPricePeriod}
            style={{ width: 120 }}
            options={[
              { label: 'Monthly', value: 'monthly' },
              { label: 'Yearly', value: 'yearly' },
              { label: '一次性', value: 'one_time' },
            ]}
          />
          <Button loading={addingPrice} onClick={() => void addPrice()} disabled={availableCurrenciesForNewPrice.length === 0}>
            + 添加币种价格
          </Button>
        </Space>

        <div style={{ marginTop: 16, borderTop: `1px solid ${platformColors.border}`, paddingTop: 12 }}>
          <Typography.Text strong>套餐积分</Typography.Text>
          <Form form={creditForm} layout="inline" style={{ marginTop: 8 }}>
            <Form.Item name="base_credits" label="基础积分" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="bonus_credits" label="赠送积分" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: 130 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" loading={savingCredits} onClick={() => void saveCredits()}>
                保存积分
              </Button>
            </Form.Item>
          </Form>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            到账总积分：{formatCredits(totalCredits)}
          </Typography.Text>
        </div>

        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
          1. 基础积分计入用户有效积分，赠送积分同样可用于所有功能。
          <br />
          2. 不同币种可设置不同的销售价格，但对应的积分数量相同（基础/赠送积分与货币无关）。
          <br />
          3. 修改套餐价格不会影响历史已购买的套餐，新的价格仅对未来生效。
        </Typography.Paragraph>
      </div>

      <div style={{ flex: '1 1 260px', minWidth: 240 }}>
        <SectionCard title="积分计算预览">
          <div style={{ padding: 6 }}>
            <Form layout="vertical" size="small">
              <Form.Item label="选择币种">
                <Select
                  value={previewCurrency}
                  onChange={setPreviewCurrency}
                  options={plan.prices.map((p) => ({ label: p.currency, value: p.currency }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="当前销售价格">
                <Input disabled value={previewPrice ? formatMoneyMinor(previewPrice.amount_minor, previewPrice.currency) : '—'} />
              </Form.Item>
            </Form>
            <div style={{ fontSize: 13, lineHeight: '24px' }}>
              <div>基础积分：{formatCredits(plan.base_credits)}</div>
              <div>赠送积分：{formatCredits(plan.bonus_credits)}</div>
              <div style={{ fontWeight: 600 }}>总积分：{formatCredits(totalCredits)}</div>
              <div style={{ marginTop: 6 }}>
                等效价格：{previewPrice ? formatPricePerCredit(previewPrice.amount_minor, previewPrice.currency, totalCredits) : '—'} / 积分
              </div>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 10 }}>
              注：此为按当前套餐配置计算的预估结果，实际到账积分以支付成功后为准。
            </Typography.Text>
          </div>
        </SectionCard>
      </div>
    </div>
  )
}

function UsageLimitsForm({ plan, onPlanUpdated }: { plan: BillingPlan; onPlanUpdated: (plan: BillingPlan) => void }) {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const validityMode = Form.useWatch('credit_validity_mode', form)

  useEffect(() => {
    form.setFieldsValue({
      exhaustion_policy: plan.exhaustion_policy,
      credit_validity_mode: plan.credit_validity_mode,
      credit_validity_days: plan.credit_validity_days,
    })
  }, [plan, form])

  const save = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      const res = await platformUpdateBillingPlan(plan.key, {
        exhaustion_policy: values.exhaustion_policy,
        credit_validity_mode: values.credit_validity_mode,
        credit_validity_days: values.credit_validity_mode === 'FIXED_DAYS' ? values.credit_validity_days : null,
      })
      onPlanUpdated({ ...res.data, prices: plan.prices, credit_metrics: plan.credit_metrics })
      message.success('用量与超限设置已保存')
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
      <Form.Item
        name="exhaustion_policy"
        label="积分耗尽后的处理策略"
        extra="当该套餐用户的积分余额降至 0 时，系统采取的动作。"
      >
        <Radio.Group>
          <Space direction="vertical">
            {Object.entries(EXHAUSTION_POLICY_LABEL).map(([value, label]) => (
              <Radio key={value} value={value}>
                <b>{value}</b> — {label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      <Form.Item name="credit_validity_mode" label="积分有效期">
        <Radio.Group>
          <Space direction="vertical">
            {Object.entries(CREDIT_VALIDITY_LABEL).map(([value, label]) => (
              <Radio key={value} value={value}>
                {label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      {validityMode === 'FIXED_DAYS' && (
        <Form.Item name="credit_validity_days" label="有效天数" rules={[{ required: true, message: '请输入有效天数' }]}>
          <InputNumber min={1} style={{ width: 200 }} addonAfter="天" />
        </Form.Item>
      )}
      <Button type="primary" loading={saving} onClick={() => void save()}>
        保存用量与超限设置
      </Button>
    </Form>
  )
}
